import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import { createAlcoholConsumed, updateAlcoholConsumed } from '../../../../store/participantService';
import { todayDate } from '../../../../utils/dateFunctions';
import { formatFloat } from '../../../../utils/numberUtils';
import DateSelect from '../../../formElements/DateSelect';
import TextInputField from '../../../formElements/TextInputField';
import FormActions from '../../../ui/formActions/FormActions';

import { validate } from './validateAlcoholConsumed';

import app from '../../../../app.module.css';
import form from '../../../../commonStyles/formStyles.module.css';

const CreateAlcoholConsumed = ({ participant, onClose, isDisabled, formType, row = {} }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const _MESSAGE = `Participant alcohol consumed entry has been ${formType === 'create' ? 'added' : 'updated'}`;
    const initialState = {
        id: '',
        participantId: '',
        unitConsumed: '',
        eventDate: todayDate()
    };

    const [newEntry, setNewEntry] = useState(initialState);
    const [initialRow, setInitialRow] = useState({});
    const [errors, setErrors] = useState({});
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // USE EFFECTS
    useEffect(() => {
        if (formType === 'edit') {
            setNewEntry(row);
            setInitialRow(row);
        }
    }, [row]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { ...errors[key], error: false } }));
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onUnitChange = (e) => {
        const value = formatFloat(e.target.value);
        onUpdate('unitConsumed', +value > 999.99 ? 999.99 : value);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);
        const newErrors = validate(newEntry, formType, initialRow);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        const payload = { ...newEntry, participantId: participant?.id };
        formType === 'create'
            ? dispatch(createAlcoholConsumed(payload, _MESSAGE))
            : dispatch(updateAlcoholConsumed(payload.id, payload, _MESSAGE));
        onClose();
    };

    // RENDER
    return (
        <div className={`${form.formWrapper} ${app.fadein}`}>
            <form
                className={form.form}
                onSubmit={onSubmit}
                data-testid="edit_alcohol_consumed_form">
                <h2 className={app.sectionHeading}>
                    {formType === 'create' ? 'Add a New Alcohol Consumed' : 'Edit Alcohol Consumed'}
                </h2>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <TextInputField
                            id="unitConsumed"
                            label="Units consumed in last seven days"
                            placeholder="Enter units..."
                            disabled={isDisabled}
                            mandatory={true}
                            maxLength={newEntry.unitConsumed >= 100 ? 6 : 5}
                            value={newEntry.unitConsumed}
                            error={errors.unitConsumed}
                            onChange={onUnitChange}
                        />
                    </div>
                    <div className={form.formColumn}>
                        <DateSelect
                            value={newEntry.eventDate}
                            label="Event Date"
                            disabled={isDisabled}
                            mandatory={true}
                            error={errors.eventDate}
                            onDateChange={(date) => onUpdate('eventDate', date)}
                        />
                    </div>
                </div>

                <FormActions
                    onClose={onSubmit}
                    onCancel={onClose}
                    disabled={isButtonDisabled || isDisabled}
                    btnText={formType === 'create' ? 'Save' : 'Update'}
                    customClass="noTopBorder"
                    error={errors?.button}
                    clearError={() => clearError('button')}
                />
            </form>
        </div>
    );
};

CreateAlcoholConsumed.propTypes = {
    participant: PropTypes.object.isRequired,
    onClose: PropTypes.func,
    isDisabled: PropTypes.bool,
    formType: PropTypes.string,
    row: PropTypes.object
};

export default CreateAlcoholConsumed;
